import { type FormikHelpers } from 'formik'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { ExternalBankAccount } from '@webapp/platform/banking/entities/ExternalBankAccount'
import messages from './messages'
import { useMutateCreateExternalBankAccount } from '../../models/useMutateCreateExternalBankAccount'
import { type ExternalBankAccountFormSchema } from '../../views/ExternalBankAccountForm'
import {
  type ExternalBankAccountFormField,
  type ExternalBankAccountFormStatus,
  getRenderedExternalBankAccountFormFields,
} from '../../views/ExternalBankAccountForm/ExternalBankAccountFormSchema'

export interface IUseCreateExternalBankAccountFormPresenterParams {
  restrictTypeToOther: boolean
  onSuccess: (account: ExternalBankAccount, createAnother: boolean) => void
}

// DRF field-error payload shape carried on RepoError.response (BHV-021
// analogue for the bank form).
interface FieldErrorResponse {
  errors?: Record<string, { code?: string; message?: string }>
}

// Server error keys are the serializer's snake_case field names. The
// type-specific keys come from PaymentMethodDataSerializer, which the backend
// validates on its own, so they arrive top-level rather than under `data`.
const SERVER_ERROR_KEYS_BY_FIELD: Record<ExternalBankAccountFormField, string> =
  {
    name: 'name',
    type: 'type',
    currency: 'currency',
    description: 'description',
    startNumber: 'start_number',
    bankName: 'bank_name',
    bankRoutingNumber: 'bank_routing_number',
    companyName: 'company_name',
    companyRoutingNumber: 'company_routing_number',
    companyTaxId: 'company_tax_id',
    glCode: 'gl_code',
  }

const FORM_FIELDS = Object.keys(
  SERVER_ERROR_KEYS_BY_FIELD
) as ExternalBankAccountFormField[]

const findFormField = (serverErrorKey: string) =>
  FORM_FIELDS.find(
    (field) => SERVER_ERROR_KEYS_BY_FIELD[field] === serverErrorKey
  )

export const useCreateExternalBankAccountFormPresenter = ({
  restrictTypeToOther,
  onSuccess,
}: IUseCreateExternalBankAccountFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreateExternalBankAccount()

  // Sets each server error on its field when the form renders that field,
  // and records the errors with the rejected values in the form's status so
  // the form keeps each one until its field changes. An error on a hidden or
  // unknown field comes back as a message to toast.
  const applyServerFieldErrors = (
    err: unknown,
    values: ExternalBankAccountFormSchema,
    helpers: FormikHelpers<ExternalBankAccountFormSchema>
  ): { appliedInline: boolean; unshownMessages: string[] } => {
    const serverErrors: ExternalBankAccountFormStatus['serverErrors'] = {}
    const unshownMessages: string[] = []

    const response =
      err instanceof RepoError
        ? (err.response as FieldErrorResponse | undefined)
        : undefined

    const renderedFields = getRenderedExternalBankAccountFormFields(
      values.type,
      restrictTypeToOther
    )

    Object.entries(response?.errors ?? {}).forEach(
      ([serverErrorKey, fieldError]) => {
        if (!fieldError?.message) return

        const field = findFormField(serverErrorKey)

        if (field && renderedFields.includes(field)) {
          helpers.setFieldError(field, fieldError.message)
          serverErrors[field] = fieldError.message
        } else {
          unshownMessages.push(fieldError.message)
        }
      }
    )

    const status: ExternalBankAccountFormStatus = {
      serverErrors,
      rejectedValues: values,
    }

    helpers.setStatus(status)

    return {
      appliedInline: Object.keys(serverErrors).length > 0,
      unshownMessages,
    }
  }

  // Resolves whether the account saved. Formik keeps the form disabled until
  // this settles; after a failure the form then moves focus back into itself.
  const handleSubmit = async (
    values: ExternalBankAccountFormSchema,
    helpers: FormikHelpers<ExternalBankAccountFormSchema>
  ): Promise<boolean> => {
    if (!values.type || !values.currency) return false

    try {
      // Id extraction at the submit boundary — option objects reduce to
      // scalars for the repository input.
      const result = await mutateAsync({
        name: values.name,
        type: values.type.value,
        currencyId: values.currency.id,
        glCode: values.glCode,
        description: values.description,
        startNumber: values.startNumber,
        bankName: values.bankName,
        bankRoutingNumber: values.bankRoutingNumber,
        companyName: values.companyName,
        companyRoutingNumber: values.companyRoutingNumber,
        companyTaxId: values.companyTaxId,
      })

      const account = new ExternalBankAccount(result)

      toast.success({
        title: intl.formatMessage(messages.successToastTitle),
        children: intl.formatMessage(messages.successToastMessage, {
          name: account.name,
        }),
      })

      onSuccess(account, values.createAnother)

      return true
    } catch (err) {
      const { appliedInline, unshownMessages } = applyServerFieldErrors(
        err,
        values,
        helpers
      )

      if (appliedInline && unshownMessages.length === 0) return false

      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          unshownMessages.length > 0
            ? unshownMessages.join(' ')
            : (getMessageFromError(err) ??
              intl.formatMessage(messages.errorToastMessage)),
      })

      return false
    }
  }

  return {
    states: {
      isSubmitting: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
